'use client'

import { Button } from '@/components/ui/button'
import { Send, Sparkles, ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { FEEDBACK_TYPES, FEEDBACK_LABELS, type FeedbackType } from '@/types'
import { InlineSheet } from './inline-sheet'

interface FeedbackSectionProps {
  artistName: string
  isFeedbackSheetOpen: boolean
  onOpenFeedbackSheet: () => void
  onCloseFeedbackSheet: () => void
  onSendFeedback: (type: FeedbackType) => void
}

export function FeedbackSection({
  artistName,
  isFeedbackSheetOpen,
  onOpenFeedbackSheet,
  onCloseFeedbackSheet,
  onSendFeedback,
}: FeedbackSectionProps) {
  const t = useTranslations('demo')
  return (
    <>
      <div data-coach="send-feedback">
        <div className="flex items-center gap-2 mb-3">
          <Send className="h-4 w-4 text-zinc-400" />
          <span className="font-semibold text-white">{t('sendFeedback')}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            onClick={() => onSendFeedback('amazing_performance' as FeedbackType)}
          >
            {t('amazingPerformance')} 🎸
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            onClick={() => onSendFeedback('love_the_energy' as FeedbackType)}
          >
            {t('loveTheEnergy')} ⚡
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            onClick={() => onSendFeedback('incredible_voice' as FeedbackType)}
          >
            {t('incredibleVoice')} 🎤
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
            onClick={onOpenFeedbackSheet}
          >
            <Sparkles className="h-3 w-3 mr-1" />
            {t('moreWaysToCheer')}
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          {t('feedbackMeansTheWorld')} 💜
        </p>
      </div>

      {/* Feedback Sheet */}
      <InlineSheet
        open={isFeedbackSheetOpen}
        onClose={onCloseFeedbackSheet}
        title={t('sendFeedback')}
        description={t('feedbackSheetDescription', { artistName })}
      >
        <div className="p-4 space-y-2">
          {FEEDBACK_TYPES.map(type => (
            <Button
              key={type}
              variant="outline"
              className="w-full justify-start border-zinc-700 hover:bg-zinc-800 h-auto py-3"
              onClick={() => onSendFeedback(type as FeedbackType)}
            >
              {FEEDBACK_LABELS[type as FeedbackType]}
            </Button>
          ))}
        </div>
      </InlineSheet>
    </>
  )
}
